// Whether the market-timing entry score may influence decisions, per series
// family — the outcome of the owner-approved, pre-registered backtest rule
// (backtest/timing.ts; result in backtest/timing_verdict.json and REPORT.md).
//
// Rule: favourable must beat stretched on mean 12-month forward return with a
// 95% block-bootstrap interval above zero. Otherwise the score is shown for
// information only and never moves a verdict, ranking or blend.
//
// Backtest of 2026-10-05 (data to 2026-09):
//   HDB (flat-type medians, 1990–2026): favourable −2.9% vs stretched +11.5%
//     forward → difference −14.4 pts [−17.6, −11.0]. Fails — the score points
//     the wrong way (HDB prices trend; "stretched" kept rising).
//   Private (URA price index, quarterly, 1975–2026): +6.4 pts [+0.001, +13.2].
//     Passes, but only just (the interval's lower end is ~0) and rank
//     correlation is not significant — re-checked by the monthly backtest.
//   COE / cars: not backtested; unchanged.

export type Family = "hdb" | "private" | "car";

export const TIMING_RULE: Record<Family, { influences: boolean; evidence: string }> = {
  hdb: {
    influences: false,
    evidence: "Backtest 1990–2026: for HDB the entry score did not predict better entries — favourable signals were followed by −2.9% over 12 months vs +11.5% after stretched ones. Shown for information only.",
  },
  private: {
    influences: true,
    evidence: "Backtest 1975–2026 (URA price index): favourable entries beat stretched by 6.4 pts of 12-month return, but the margin is fragile (interval lower bound ≈ 0).",
  },
  car: { influences: true, evidence: "Not backtested (COE)." },
};

export function familyOf(seriesId: string | undefined): Family {
  if (seriesId?.startsWith("hdb")) return "hdb";
  if (seriesId?.startsWith("coe") || seriesId?.startsWith("car")) return "car";
  return "private";
}

export function timingInfluences(seriesId: string | undefined): boolean {
  return TIMING_RULE[familyOf(seriesId)].influences;
}

/** The entry score as a decision input: null when the backtest says it isn't predictive for this family. */
export function decisionScore(seriesId: string | undefined, score: number | null | undefined): number | null {
  return timingInfluences(seriesId) ? (score ?? null) : null;
}
